import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@jsverse/transloco';

export interface ErrorMessage {
  message: string;
  visible: boolean;
}

/**
 * Standardowy format błędu z backendu
 */
export interface BackendErrorResponse {
  statusCode: number;
  timestamp: string;
  path: string;
  errorCode: string;
  message: string;
  error: string;
  data?: Record<string, any> | null;
  validationErrors?: ValidationError[] | null;
}

export interface ValidationError {
  field: string;
  message: string;
  code?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  errorMessage: ErrorMessage = { message: '', visible: false };

  constructor(
    private _messageService: MessageService,
    private _translocoService: TranslocoService
  ) {}

  catchErrorBackend(error: any): Observable<any> {
    console.log('🔴 ErrorHandlerService.catchErrorBackend - otrzymany błąd:', error);
    
    const summary = this._translocoService.translate('errors.errorMessageTitle');
    
    // Pobierz errorCode w formacie "CATEGORY.ERROR_NAME" np. "USER.EMAIL_ALREADY_TAKEN"
    const errorCode = error?.errorCode;
    const data = error?.data ?? {};
    const validationErrors = error?.validationErrors;

    console.log('📌 errorCode:', errorCode);
    console.log('📌 data:', data);

    // Zbuduj parametry do transloco z obiektu data
    const params: Record<string, any> = { ...data };

    let translated = '';

    // 1) Spróbuj dopasować tłumaczenie po errorCode — najpierw wariant dla powodu z `data.reason`
    //    (np. 409 INVALID_STATE z reason=free_text_changed), potem ogólny komunikat kodu
    if (errorCode) {
      for (const translationKey of this._buildTranslationKeys(errorCode, data?.reason)) {
        console.log('🔑 Klucz tłumaczenia:', translationKey);

        const translatedText = this._translocoService.translate(translationKey, params);
        console.log('📝 Przetłumaczony tekst:', translatedText);

        if (translatedText !== translationKey) {
          translated = translatedText;
          break;
        }
      }
    }

    // 2) Obsłuż błędy walidacji
    if (!translated && validationErrors && validationErrors.length > 0) {
      translated = this._handleValidationErrors(validationErrors);
    }

    // 3) Jeżeli brak errorCode lub brak tłumaczenia, spróbuj po statusie HTTP
    if (!translated) {
      const status = error?.status || error?.statusCode || 0;
      const statusKey = `errors.httpStatus.${status}`;
      const statusTranslated = this._translocoService.translate(statusKey, params);
      if (statusTranslated !== statusKey) {
        translated = statusTranslated;
      }
    }

    // 4) Ostateczny fallback na komunikat ogólny
    if (!translated) {
      translated = this._translocoService.translate('errors.COMMON.INTERNAL_ERROR');
    }

    this.setErrorMessage(translated);
    this._messageService.add({ severity: 'error', summary, detail: translated });

    return throwError(() => new Error(translated));
  }

  /**
   * Buduje klucz tłumaczenia z errorCode
   * np. "USER.EMAIL_ALREADY_TAKEN" -> "errors.USER.EMAIL_ALREADY_TAKEN"
   */
  private _buildTranslationKey(errorCode: string): string {
    return `errors.${errorCode}`;
  }

  /**
   * Klucze tłumaczenia errorCode od najbardziej szczegółowego. Gdy backend podaje powód
   * (`data.reason`, np. "free_text_changed"), najpierw `errors.<KOD>_REASONS.<powód>`:
   * "REQUEST_BOARD.INVALID_STATE" + "free_text_changed" →
   * ["errors.REQUEST_BOARD.INVALID_STATE_REASONS.free_text_changed", "errors.REQUEST_BOARD.INVALID_STATE"].
   * Brak tłumaczenia wariantu = ogólny komunikat kodu (bez zmian dla pozostałych błędów).
   */
  private _buildTranslationKeys(errorCode: string, reason: unknown): string[] {
    const base = this._buildTranslationKey(errorCode);
    if (typeof reason === 'string' && /^[A-Za-z0-9_-]+$/.test(reason)) {
      return [`${base}_REASONS.${reason}`, base];
    }
    return [base];
  }

  /**
   * Obsługuje błędy walidacji i zwraca przetłumaczony komunikat
   */
  private _handleValidationErrors(validationErrors: ValidationError[]): string {
    const translatedErrors = validationErrors.map((ve) => {
      if (ve.code) {
        const key = this._buildTranslationKey(ve.code);
        const params = { field: ve.field };
        const translated = this._translocoService.translate(key, params);
        if (translated !== key) {
          return translated;
        }
      }
      return ve.message;
    });

    return translatedErrors.join('. ');
  }

  /**
   * Pobiera errorCode z ostatniego błędu (przydatne dla komponentów)
   */
  getErrorCode(error: any): string | undefined {
    return error?.errorCode;
  }

  /**
   * Pobiera dane dodatkowe z błędu
   */
  getErrorData(error: any): Record<string, any> | null {
    return error?.data ?? null;
  }

  /**
   * Pobiera błędy walidacji z odpowiedzi
   */
  getValidationErrors(error: any): ValidationError[] | null {
    return error?.validationErrors ?? null;
  }

  setErrorMessage(message: string): void {
    this.errorMessage = { message: message, visible: true };
  }

  clearErrorMessage(): void {
    this.errorMessage = { message: '', visible: false };
  }

  getErrorMessage(): ErrorMessage {
    return this.errorMessage;
  }
}
